import { createSlice } from '@reduxjs/toolkit';
import {
  attachAdminSessionToLoginPayload,
  buildAdminSessionFromUser,
  createAccessContext,
  FULL_ACCESS_ACCOUNT_ID,
  getAdminSessionFromLogin,
  normalizeRoleAccess,
  resolveSwitchedRole,
} from '../../utils/rolePermissions';

const initialState = {
  activeRole: null,
  availableRoles: [],
};

function readLoginSession() {
  try {
    return JSON.parse(localStorage.getItem('LoginSession') || 'null');
  } catch {
    return null;
  }
}

function persistActiveRoleToLogin(activeRole) {
  const login = readLoginSession();
  if (!login) return;
  const nextLogin = attachAdminSessionToLoginPayload(login, {
    activeRole,
    availableRoles: login.adminAvailableRoles || login.user?.adminAvailableRoles || [],
  });
  localStorage.setItem('LoginSession', JSON.stringify(nextLogin));
}

const adminAccessSlice = createSlice({
  name: 'adminAccess',
  initialState,
  reducers: {
    setAdminSession: (state, action) => {
      const activeRole = normalizeRoleAccess(action.payload?.activeRole);
      const availableRoles = (action.payload?.availableRoles || []).map(normalizeRoleAccess);
      state.activeRole = activeRole;
      state.availableRoles = availableRoles;
    },
    initializeAdminSessionFromLogin: (state, action) => {
      const login = action.payload || readLoginSession() || {};
      const session = getAdminSessionFromLogin(login);
      state.activeRole = session.activeRole;
      state.availableRoles = session.availableRoles;

      // Backfill switch accounts into LoginSession for existing sessions.
      const hasStoredRoles = login.adminAvailableRoles?.length || login.user?.adminAvailableRoles?.length;
      if (session.availableRoles.length && !hasStoredRoles) {
        const nextLogin = attachAdminSessionToLoginPayload(login, session);
        localStorage.setItem('LoginSession', JSON.stringify(nextLogin));
      }
    },
    switchAdminRole: (state, action) => {
      const accountId = action.payload?.accountId ?? action.payload ?? FULL_ACCESS_ACCOUNT_ID;
      const login = readLoginSession();
      const fallbackUser = login?.user || login || {};
      const nextRole = resolveSwitchedRole(accountId, state.availableRoles, fallbackUser);
      state.activeRole = normalizeRoleAccess(nextRole);
      persistActiveRoleToLogin(state.activeRole);
    },
    resetAdminSession: () => initialState,
  },
});

export const {
  setAdminSession,
  initializeAdminSessionFromLogin,
  switchAdminRole,
  resetAdminSession,
} = adminAccessSlice.actions;

export const selectActiveRole = (state) => state.adminAccess.activeRole;
export const selectAvailableRoles = (state) => state.adminAccess.availableRoles;
export const selectIsFullAccess = (state) => Boolean(state.adminAccess.activeRole?.isFullAccess);
export const selectActiveAccountId = (state) => state.adminAccess.activeRole?.accountId || null;

export const selectAccessContext = (state) =>
  createAccessContext(state.adminAccess.activeRole);

export const selectActiveAdminAccount = selectActiveRole;
export const selectAdminSwitchAccounts = selectAvailableRoles;
export const selectIsAdminOwnerView = selectIsFullAccess;
export const selectAdminAccessContext = selectAccessContext;
export const selectAdminActiveAccountId = selectActiveAccountId;

export function createAdminSessionForLogin(user = {}) {
  return buildAdminSessionFromUser(user);
}

export default adminAccessSlice.reducer;
